// components/NewRemindersGuideModal.tsx
// Modal-guía tipo wizard que le explica a la secretaria cómo funciona ahora
// el flujo de recordatorios manuales por WhatsApp. Se muestra una sola vez
// (flag en localStorage) pero se puede re-abrir desde el dashboard.

import React, { useState } from 'react';
import {
  X, ArrowRight, ArrowLeft, Bell, MessageCircle, CheckCircle,
  Send, XCircle, Clock, Phone, FileText, PartyPopper,
} from 'lucide-react';

interface NewRemindersGuideModalProps {
  open: boolean;
  onClose: () => void;
}

const STORAGE_KEY = 'consultar.reminders_guide_seen_v1';

export const hasSeenRemindersGuide = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const markRemindersGuideSeen = (): void => {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* noop */
  }
};

const TOTAL_STEPS = 5;

const NewRemindersGuideModal: React.FC<NewRemindersGuideModalProps> = ({ open, onClose }) => {
  const [step, setStep] = useState(0);

  if (!open) return null;

  const handleClose = () => {
    markRemindersGuideSeen();
    setStep(0);
    onClose();
  };

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col overflow-hidden max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Bell className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Cómo funcionan ahora los recordatorios
              </h3>
              <p className="text-xs text-gray-500">Paso {step + 1} de {TOTAL_STEPS}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator (dots) */}
        <div className="px-5 pt-4 flex items-center gap-1.5">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= step ? 'bg-emerald-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step body */}
        <div className="px-5 py-5 overflow-y-auto">
          {step === 0 && <StepIntro />}
          {step === 1 && <StepOpen />}
          {step === 2 && <StepList />}
          {step === 3 && <StepSend />}
          {step === 4 && <StepMark />}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <button
            onClick={prev}
            disabled={step === 0}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" /> Atrás
          </button>

          {step < TOTAL_STEPS - 1 ? (
            <button
              onClick={next}
              className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
            >
              Siguiente <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
            >
              <CheckCircle className="w-4 h-4" /> Entendido, ¡empezar!
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================================
// PASOS
// =====================================================

const StepIntro: React.FC = () => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <PartyPopper className="w-5 h-5 text-amber-500" />
      <h4 className="text-base font-semibold text-gray-900">Novedad importante</h4>
    </div>
    <p className="text-sm text-gray-700 leading-relaxed">
      Los recordatorios y confirmaciones por WhatsApp ahora los mandás
      <strong> vos desde tu propio WhatsApp Web</strong>. El sistema te arma
      la lista de turnos y te prepara el mensaje. Vos solo apretás y mandás.
    </p>

    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <p className="text-sm font-medium text-emerald-900 mb-2">¿Por qué cambió?</p>
      <ul className="space-y-1.5 text-sm text-emerald-900">
        <li className="flex gap-2">
          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>El paciente ve <strong>tu número real</strong> y puede responderte.</span>
        </li>
        <li className="flex gap-2">
          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Si cancela por chat, te enterás al toque.</span>
        </li>
        <li className="flex gap-2">
          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Sin costo por mensaje.</span>
        </li>
      </ul>
    </div>
  </div>
);

const StepOpen: React.FC = () => (
  <div>
    <h4 className="text-base font-semibold text-gray-900 mb-2">
      1. Abrí el panel de recordatorios
    </h4>
    <p className="text-sm text-gray-700 mb-4">
      Vas a ver un botón verde <strong>Recordatorios</strong> arriba en el dashboard.
      Clickealo cada día (idealmente al empezar la jornada).
    </p>

    {/* Mockup del botón */}
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex items-center gap-4">
        <div className="text-xs text-gray-400">Dashboard</div>
        <button
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium pointer-events-none"
          tabIndex={-1}
        >
          <MessageCircle className="w-4 h-4" />
          Recordatorios
        </button>
        <div className="w-6 h-6 rounded-full bg-gray-200" />
      </div>
    </div>

    <p className="text-xs text-gray-500 mt-3">
      Si tenés varios profesionales asignados, primero elegí uno con el filtro.
    </p>
  </div>
);

const StepList: React.FC = () => (
  <div>
    <h4 className="text-base font-semibold text-gray-900 mb-2">
      2. Mirá qué turnos hay que avisar
    </h4>
    <p className="text-sm text-gray-700 mb-4">
      El panel tiene dos pestañas: <strong>Mañana</strong> (para mandar los
      recordatorios 24hs antes) y <strong>Hoy</strong> (para confirmaciones
      de último momento).
    </p>

    {/* Mockup del panel */}
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <div className="flex-1 py-2 text-center text-sm font-medium text-emerald-700 border-b-2 border-emerald-500 bg-emerald-50/40">
          Mañana (3)
        </div>
        <div className="flex-1 py-2 text-center text-sm text-gray-500">
          Hoy (1)
        </div>
      </div>

      {/* Turno card mockup */}
      <div className="p-3 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Clock className="w-3.5 h-3.5" />
          09:00 · 30 min
        </div>
        <p className="font-medium text-gray-900 text-sm">Juan Pérez</p>
        <p className="text-xs text-gray-600">Consulta general</p>
        <div className="flex items-center gap-1 text-xs text-emerald-700 mt-1">
          <Phone className="w-3 h-3" />
          11 2233-4455
        </div>
      </div>
      <div className="p-3 bg-white">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Clock className="w-3.5 h-3.5" />
          10:30 · 45 min
        </div>
        <p className="font-medium text-gray-900 text-sm">María López</p>
        <p className="text-xs text-gray-600">Control anual</p>
      </div>
    </div>
  </div>
);

const StepSend: React.FC = () => (
  <div>
    <h4 className="text-base font-semibold text-gray-900 mb-2">
      3. Abrí WhatsApp con un clic
    </h4>
    <p className="text-sm text-gray-700 mb-4">
      Elegí la plantilla (por defecto <strong>Recordatorio 24hs</strong>) y
      clickeá <strong>WhatsApp</strong>. Se abre tu WhatsApp Web con el
      mensaje ya escrito y listo para enviar.
    </p>

    {/* Mockup */}
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
      {/* Dropdown plantilla */}
      <div className="p-3 border-b border-gray-100">
        <label className="text-xs text-gray-500 mb-1 block">Plantilla:</label>
        <div className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white">
          <FileText className="w-4 h-4 text-gray-400" />
          Recordatorio 24hs
        </div>
      </div>

      {/* Botones de acción */}
      <div className="p-3 flex flex-wrap gap-2">
        <button
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium pointer-events-none"
          tabIndex={-1}
        >
          <FileText className="w-3.5 h-3.5" />
          Ver mensaje
        </button>
        <button
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium pointer-events-none"
          tabIndex={-1}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          WhatsApp
        </button>
      </div>

      {/* Preview del mensaje */}
      <div className="mx-3 mb-3 p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
        <p className="text-xs text-emerald-900 leading-relaxed">
          Hola <strong>Juan</strong>! 👋<br />
          Te recordamos tu turno con <strong>Dra. Sappa</strong> para mañana
          <strong> viernes 17 de abril</strong> a las <strong>09:00</strong>.<br />
          📍 Av. Siempre Viva 1234<br />
          Por favor confirmanos respondiendo este mensaje.
        </p>
      </div>
    </div>

    <p className="text-xs text-gray-500 mt-3">
      Una vez que clickeás WhatsApp, el turno queda marcado como <strong>enviado</strong>.
    </p>
  </div>
);

const StepMark: React.FC = () => (
  <div>
    <h4 className="text-base font-semibold text-gray-900 mb-2">
      4. Marcá la respuesta del paciente
    </h4>
    <p className="text-sm text-gray-700 mb-4">
      Cuando el paciente te responda, marcá qué pasó. Así el profesional ve
      en su calendario el estado de cada turno.
    </p>

    <div className="space-y-2.5">
      <div className="flex items-start gap-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50">
        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-gray-900">Confirmó</p>
          <p className="text-xs text-gray-600">
            El turno pasa a estado <em>confirmado</em> automáticamente.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-xl border border-red-200 bg-red-50">
        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-gray-900">Canceló</p>
          <p className="text-xs text-gray-600">
            El turno se cancela y el horario queda libre para otro paciente.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-xl border border-amber-200 bg-amber-50">
        <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-gray-900">Sin respuesta</p>
          <p className="text-xs text-gray-600">
            Queda registrado que no contestó, pero el turno sigue vigente.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-xl border border-sky-200 bg-sky-50">
        <Send className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-gray-900">Ya enviado</p>
          <p className="text-xs text-gray-600">
            Si todavía no respondió, lo dejás como enviado y lo revisás más tarde.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default NewRemindersGuideModal;
